import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  getItems,
  getItemStocks,
  getItemSales,
  getItemSalesReturns,
  getItemPurchase,
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";
import PieChart from "../../Common/charts/PieChart";
import { useNavigate } from "react-router-dom";

const ItemDashboard = () => {
  const navigate = useNavigate();
  const { subCompany, UserItem } = useContext(WarehouseContext);
  const currentShopId = subCompany?.id;

  const [salesList, setSalesList] = useState([]);
  const [stocksList, setStocksList] = useState([]);
  const [returnsList, setReturnsList] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [totalOrders, setTotalOrders] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [soldItemsCount, setSoldItemsCount] = useState(0);
  const [totalReturnsCount, setTotalReturnsCount] = useState(0);
  const [totalStocksQty, setTotalStocksQty] = useState(0);

  const [mostLoyalCustomers, setMostLoyalCustomers] = useState([]);
  const [mostSellingItems, setMostSellingItems] = useState([]);

  const rolePermissions = UserItem?.menu_list ? JSON.parse(UserItem.menu_list) : [];
  const role = UserItem?.role_title?.toLowerCase();
  const isAdminOrSuper = role === "super admin" || role === "admin";

  const hasRoutePermission = (route) => {
    if (isAdminOrSuper) return true;
    if (["/ItemPurchase", "/ItemSales", "/item_sales_list", "/ItemStock_list"].includes(route)) {
      return rolePermissions.some((r) => r.menu === "/Item_list");
    }
    return rolePermissions.some((r) => r.menu === route);
  };

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    try {
      // 1. Fetch Item Sales
      const salesRes = await getItemSales(null, null, currentShopId);
      const salesData = salesRes?.data?.data || [];
      setSalesList(salesData);

      const count = salesData.length;
      setTotalOrders(count);

      let rev = 0;
      let totalSold = 0;
      const customerMap = {};
      const itemMap = {};

      salesData.forEach((order) => {
        const orderPrice = parseFloat(order.overall_price || order.total_price || order.price || 0);
        rev += orderPrice;

        const custName = order.customer_name || "Walk-in Customer";
        customerMap[custName] = (customerMap[custName] || 0) + 1;

        if (Array.isArray(order.details)) {
          order.details.forEach((det) => {
            const qty = parseFloat(det.qty || 0);
            totalSold += qty;

            const itemTitle = det.product_title || `Item #${det.item_id}`;
            itemMap[itemTitle] = (itemMap[itemTitle] || 0) + qty;
          });
        }
      });

      setTotalRevenue(rev);
      setSoldItemsCount(totalSold);

      // Customer Chart
      const custChartData = Object.keys(customerMap).map((key) => ({
        title: key,
        value: customerMap[key],
      }));
      setMostLoyalCustomers(custChartData);

      // Item Chart
      const itemChartData = Object.keys(itemMap).map((key) => ({
        title: key,
        value: itemMap[key],
      }));
      setMostSellingItems(itemChartData);

      // 2. Fetch Item Stocks
      const stockRes = await getItemStocks(null, null);
      const stockData = stockRes?.data?.data || [];
      setStocksList(stockData);

      let sumStock = 0;
      stockData.forEach((st) => {
        sumStock += parseFloat(st.remaining || st.qty || 0);
      });
      setTotalStocksQty(sumStock);

      // 3. Fetch Item Sales Returns
      const returnRes = await getItemSalesReturns(null, null, currentShopId);
      const returnData = returnRes?.data?.data || [];
      setReturnsList(returnData);
      setTotalReturnsCount(returnData.length);

      // 4. Fetch Items List
      const itemsRes = await getItems();
      const itemsData = itemsRes?.data?.data || [];
      setItemsList(itemsData);
    } catch (err) {
      console.error("Error fetching Item Dashboard data:", err);
    } finally {
      setLoading(false);
    }
  }, [currentShopId]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const visibleActionsCount = [
    hasRoutePermission("/ItemPurchase"),
    hasRoutePermission("/ItemSales"),
    hasRoutePermission("/salary_list"),
    hasRoutePermission("/expense"),
  ].filter(Boolean).length;

  return (
    <div
      className="space-y-6 p-6 dashboard-container"
      style={{
        fontFamily: 'Poppins, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <style>{`
        .dashboard-container {
          background-color: #F8F9FA !important;
          min-height: 100vh;
        }
        .dashboard-card {
          background: #ffffff !important;
          border: 1px solid #E2E8F0 !important;
          border-radius: 12px !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.02), 0 1px 2px rgba(0,0,0,0.01) !important;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
          overflow: hidden;
          margin-bottom: 0 !important;
        }
        .dashboard-card:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05) !important;
        }
        .metric-title {
          color: #64748B !important;
          font-size: 0.72rem !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          letter-spacing: 0.07em !important;
          margin-bottom: 0.25rem !important;
        }
        .metric-value {
          color: #0F172A !important;
          font-size: 1.65rem !important;
          font-weight: 700 !important;
          letter-spacing: -0.03em !important;
          margin: 0 !important;
        }
        .metric-icon-wrapper {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          width: 44px !important;
          height: 44px !important;
          border-radius: 10px !important;
          font-size: 1.3rem !important;
          transition: all 0.2s ease !important;
        }
        .dashboard-table {
          width: 100% !important;
          border-collapse: collapse !important;
        }
        .dashboard-table th {
          text-align: left !important;
          font-size: 0.75rem !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          color: #475569 !important;
          background-color: #F8FAFC !important;
          border-bottom: 1px solid #E2E8F0 !important;
          padding: 14px 20px !important;
          letter-spacing: 0.05em !important;
        }
        .dashboard-table td {
          text-align: left !important;
          font-size: 0.85rem !important;
          color: #334155 !important;
          border-bottom: 1px solid #F1F5F9 !important;
          padding: 14px 20px !important;
        }
        .dashboard-table tr:hover td {
          background-color: #F8FAFC !important;
        }
        .dashboard-card-header {
          border-bottom: 1px solid #F1F5F9 !important;
          padding: 18px 24px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: space-between !important;
          background-color: transparent !important;
        }
        .dashboard-card-title {
          font-size: 0.95rem !important;
          font-weight: 700 !important;
          color: #0F172A !important;
          margin: 0 !important;
        }
        .dashboard-table-container {
          height: 40vh;
          overflow-y: auto;
        }
        .dashboard-table-container::-webkit-scrollbar {
          width: 6px;
        }
        .dashboard-table-container::-webkit-scrollbar-track {
          background: #F8FAFC;
        }
        .dashboard-table-container::-webkit-scrollbar-thumb {
          background: #CBD5E1;
          border-radius: 3px;
        }
        .quick-action-card {
          background: #ffffff !important;
          border: 1px solid #E2E8F0 !important;
          border-radius: 12px !important;
          padding: 1.5rem 1rem !important;
          text-align: center !important;
          cursor: pointer !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          display: flex !important;
          flex-direction: column !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .quick-action-card:hover {
          transform: translateY(-2px) !important;
          border-color: #CBD5E1 !important;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -4px rgba(0, 0, 0, 0.05) !important;
          background-color: #FAFAFA !important;
        }
        .quick-action-icon {
          font-size: 1.8rem !important;
          margin-bottom: 0.5rem !important;
        }
        .quick-action-title {
          font-size: 0.9rem !important;
          font-weight: 600 !important;
          color: #334155 !important;
        }
      `}</style>

      {/* Header Section */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 mt-2 gap-3">
        <div>
          <h2 className="fw-bold text-dark mb-1" style={{ fontSize: "1.75rem", letterSpacing: "-0.025em" }}>
            Item Dashboard
          </h2>
          <p className="text-secondary mb-0" style={{ fontSize: "0.875rem" }}>
            Overview of item stocks, sales, and purchases
          </p>
        </div>

        {hasRoutePermission("/ItemSales") && (
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 fw-semibold shadow-sm"
            onClick={() => navigate("/ItemSales")}
            style={{ borderRadius: "8px" }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Item Sales Window
          </button>
        )}
      </div>

      {/* Quick Actions Section */}
      {visibleActionsCount > 0 && (
        <div className="dashboard-card quick-actions-container p-4 mb-4">
          <h5 className="fw-bold text-dark mb-4" style={{ fontSize: "1.05rem" }}>
            Quick Actions
          </h5>
          <div className="row g-3">
            {hasRoutePermission("/ItemPurchase") && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="quick-action-card" onClick={() => navigate("/ItemPurchase")}>
                  <div className="quick-action-icon text-primary">
                    <i className="bx bx-receipt" />
                  </div>
                  <span className="quick-action-title">Item Purchase</span>
                </div>
              </div>
            )}
            {hasRoutePermission("/ItemSales") && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="quick-action-card" onClick={() => navigate("/ItemSales")}>
                  <div className="quick-action-icon text-success">
                    <i className="bx bx-cart" />
                  </div>
                  <span className="quick-action-title">Item Sales Window</span>
                </div>
              </div>
            )}
            {hasRoutePermission("/salary_list") && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="quick-action-card" onClick={() => navigate("/salary_list")}>
                  <div className="quick-action-icon text-warning">
                    <i className="bx bx-wallet" />
                  </div>
                  <span className="quick-action-title">Salaries</span>
                </div>
              </div>
            )}
            {hasRoutePermission("/expense") && (
              <div className="col-12 col-md-6 col-lg-3">
                <div className="quick-action-card" onClick={() => navigate("/expense")}>
                  <div className="quick-action-icon text-danger">
                    <i className="bx bx-credit-card" />
                  </div>
                  <span className="quick-action-title">Record Expense</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Metric Cards Grid */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4 mb-4">
        {/* Card 1: Total Item Orders */}
        <div className="col">
          <div className="dashboard-card p-4">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <p className="metric-title">Total Item Orders</p>
                <h3 className="metric-value">{totalOrders}</h3>
              </div>
              <div className="metric-icon-wrapper" style={{ backgroundColor: "#EFF6FF", color: "#3B82F6" }}>
                <i className="bx bx-cart-alt" />
              </div>
            </div>
            <div className="mt-3 text-muted" style={{ fontSize: "0.75rem" }}>
              Total item orders placed
            </div>
          </div>
        </div>

        {/* Card 2: Total Item Revenue */}
        <div className="col">
          <div className="dashboard-card p-4">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <p className="metric-title">Total Sales Revenue</p>
                <h3 className="metric-value">₹ {Math.round(totalRevenue).toLocaleString()}</h3>
              </div>
              <div className="metric-icon-wrapper" style={{ backgroundColor: "#ECFDF5", color: "#10B981" }}>
                <i className="bi bi-cash" />
              </div>
            </div>
            <div className="mt-3 text-muted" style={{ fontSize: "0.75rem" }}>
              Total revenue from item sales
            </div>
          </div>
        </div>

        {/* Card 3: Available Item Stock */}
        <div className="col">
          <div className="dashboard-card p-4">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <p className="metric-title">Available Item Stock</p>
                <h3 className="metric-value">{totalStocksQty.toFixed(2)}</h3>
              </div>
              <div className="metric-icon-wrapper" style={{ backgroundColor: "#EEF2FF", color: "#6366F1" }}>
                <i className="bx bx-box" />
              </div>
            </div>
            <div className="mt-3 text-muted" style={{ fontSize: "0.75rem" }}>
              Current stock count in inventory
            </div>
          </div>
        </div>

        {/* Card 4: Sold Items Qty */}
        <div className="col">
          <div className="dashboard-card p-4">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <p className="metric-title">Sold Item Quantity</p>
                <h3 className="metric-value">{soldItemsCount}</h3>
              </div>
              <div className="metric-icon-wrapper" style={{ backgroundColor: "#FFFBEB", color: "#F59E0B" }}>
                <i className="bx bx-package" />
              </div>
            </div>
            <div className="mt-3 text-muted" style={{ fontSize: "0.75rem" }}>
              Total item units sold
            </div>
          </div>
        </div>

        {/* Card 5: Total Items Registered */}
        <div className="col">
          <div className="dashboard-card p-4">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <p className="metric-title">Registered Items</p>
                <h3 className="metric-value">{itemsList.length}</h3>
              </div>
              <div className="metric-icon-wrapper" style={{ backgroundColor: "#F5F3FF", color: "#8B5CF6" }}>
                <i className="bx bx-layer" />
              </div>
            </div>
            <div className="mt-3 text-muted" style={{ fontSize: "0.75rem" }}>
              Total item master records
            </div>
          </div>
        </div>

        {/* Card 6: Total Sales Returns */}
        <div className="col">
          <div className="dashboard-card p-4">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <p className="metric-title">Total Returns</p>
                <h3 className="metric-value">{totalReturnsCount}</h3>
              </div>
              <div className="metric-icon-wrapper" style={{ backgroundColor: "#FFF1F2", color: "#F43F5E" }}>
                <i className="bx bx-refresh" />
              </div>
            </div>
            <div className="mt-3 text-muted" style={{ fontSize: "0.75rem" }}>
              Total item sales returns
            </div>
          </div>
        </div>
      </div>

      {/* Apex Charts Section */}
      <div className="row g-4 mb-4">
        {/* Most Loyal Customers */}
        <div className="col-md-6">
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h6 className="dashboard-card-title">Top Item Customers</h6>
            </div>
            {mostLoyalCustomers && mostLoyalCustomers.length > 0 ? (
              <div className="card-body p-4">
                <PieChart data={mostLoyalCustomers} />
              </div>
            ) : (
              <div
                className="card-body p-4 d-flex align-items-center justify-content-center"
                style={{ height: "350px" }}
              >
                <div className="text-center text-muted">
                  <i className="bi bi-people fs-1 d-block mb-2 text-secondary opacity-50"></i>
                  <p className="mb-0 fw-semibold">No customer data available</p>
                  <span className="text-xs" style={{ fontSize: "0.75rem" }}>
                    Charts will appear once item sales are made
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Most Selling Items */}
        <div className="col-md-6">
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h6 className="dashboard-card-title">Top Selling Items</h6>
            </div>
            {mostSellingItems && mostSellingItems.length > 0 ? (
              <div className="card-body p-4">
                <PieChart type="polarArea" data={mostSellingItems} />
              </div>
            ) : (
              <div
                className="card-body p-4 d-flex align-items-center justify-content-center"
                style={{ height: "350px" }}
              >
                <div className="text-center text-muted">
                  <i className="bi bi-box-seam fs-1 d-block mb-2 text-secondary opacity-50"></i>
                  <p className="mb-0 fw-semibold">No selling item data available</p>
                  <span className="text-xs" style={{ fontSize: "0.75rem" }}>
                    Charts will appear once items are sold
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stock & Sales Tables */}
      <div className="row g-4 mb-4">
        {/* Item Stock Alert / Overview */}
        <div className="col-md-6">
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h6 className="dashboard-card-title">Item Stocks Log</h6>
            </div>
            <div className="card-body p-0">
              <div className="dashboard-table-container">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>#</th>
                      <th>Item Name</th>
                      <th style={{ width: "100px" }}>Remaining</th>
                      <th style={{ width: "100px" }}>Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stocksList.length > 0 ? (
                      stocksList.slice(0, 10).map((item, index) => (
                        <tr key={index}>
                          <td className="text-secondary">{index + 1}</td>
                          <td className="fw-semibold text-dark">{item?.item_name || "Item"}</td>
                          <td>
                            <span
                              className="badge bg-light text-dark px-2.5 py-1.5"
                              style={{ fontSize: "0.825rem", borderRadius: "6px", border: "1px solid #E2E8F0" }}
                            >
                              {item?.remaining !== undefined && item?.remaining !== null
                                ? parseFloat(item.remaining).toFixed(2)
                                : 0}
                            </span>
                          </td>
                          <td>
                            <span className="fw-bold text-success" style={{ fontSize: "0.825rem" }}>
                              ₹ {item?.sales_price || item?.purchase_price || 0}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-5">
                          No item stocks available
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Item Sales */}
        <div className="col-md-6">
          <div className="dashboard-card">
            <div className="dashboard-card-header">
              <h6 className="dashboard-card-title">Recent Item Sales</h6>
            </div>
            <div className="card-body p-0">
              <div className="dashboard-table-container">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>#</th>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th style={{ width: "110px" }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesList.length > 0 ? (
                      salesList.slice(0, 10).map((data, index) => (
                        <tr key={index}>
                          <td className="text-secondary">{index + 1}</td>
                          <td className="fw-semibold text-dark">{data?.order_id}</td>
                          <td>{data?.customer_name || "Walk-in"}</td>
                          <td>
                            <span className="badge bg-success bg-opacity-10 text-success px-2.5 py-1.5 fw-bold" style={{ fontSize: "0.825rem", borderRadius: "6px" }}>
                              ₹ {data?.overall_price || data?.total_price || 0}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-5">
                          No item sales found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemDashboard;
